import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/features/shared/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/features/shared/components/ui/tabs';
import { Switch } from '@/features/shared/components/ui/switch';
import { Textarea } from '@/features/shared/components/ui/textarea';
import {
  ArrowLeft, Mail, Phone, Building2, Calendar, Hash, Users, Loader2, CalendarOff,
  Network, Save, Pencil, X, User, BadgeCheck, Briefcase, MapPin, Plus, ShieldAlert, AlertTriangle,
} from 'lucide-react';
import {
  getEmployee, getAllEmployees, getDepartments, getJobTitles, getTeamOut, getOrgSummary,
  updateEmployee, getEmployeeReadiness, listBranches, createBranch,
  type Employee, type OrgSummary, type EmployeeReadiness, type Branch,
  deleteEmployeeRecord,
} from '@/lib/hrApi';
import { useHasPermission } from '@/rbac/useRbac';
import { EngagementsCard } from './engagements/EngagementsCard';
import { useAppSelector } from '@/app/hooks';
import { isValidTIN, isValidNationalId, isValidNSSF, TIN_HINT, NATIONAL_ID_HINT, NSSF_HINT } from '@/features/shared/validation/statutory';
import { isValidPhone, PHONE_HINT } from '@/features/shared/validation/phone';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { CompensationCard } from './CompensationCard';
import { PayrollProfileCard } from './components/PayrollProfileCard';
import { CostAllocationCard } from './components/CostAllocationCard';
import { EmployeePayoutMethodsCard } from './components/EmployeePayoutMethodsCard';
import { SalaryAllocationCard } from '@/features/payment-methods/components/SalaryAllocationCard';
import { ReadinessCard, ReadinessBadge } from './components/ReadinessCard';
import { EmployeeDocumentsCard } from './components/EmployeeDocumentsCard';
import { RequiredDocumentsCard } from './components/RequiredDocumentsCard';
import { DanaProfile } from './components/DanaProfile';
import { ChangeHistoryCard } from './components/ChangeHistoryCard';
import { EmployeeLifecycleCard } from './components/EmployeeLifecycleCard';
import { isPendingApproval, humanizeField } from '@/services/changeRequestService';
import { getEmployeeLifecycle, type LifecycleInstanceView } from '@/services/lifecycleService';
import { isDanaId } from '@/lib/dana';
import { ORG_LEVELS, levelLabel, levelColor } from './orgLevel';
import { isSelfRecord } from './selfView';
import { EmployeeStatusBadge } from '@/features/shared/components/EmployeeStatusBadge';
import { formatDate } from '@/lib/orgFormat';
import { isOnLeaveOn, localTodayIso, resolveEmployeeStatus } from '@/features/shared/status/employeeStatus';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/lib/apiError';
import { toDisplayDate } from '@/lib/orgFormat';
import { PhoneInput } from '@/features/shared/components/PhoneInput';

interface LeavePeriod { type?: string; start_date: string; end_date?: string; }

// Shared field styling for this page (same look as the original cards).
const inputCls =
  'w-full h-10 px-3 rounded-md border border-border bg-card text-sm disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed';
const labelCls = 'text-xs text-muted-foreground';

/** Save patch signature every editable card uses. `pending` means the edit was
 * routed to the approval workflow (HTTP 202) instead of being applied — the
 * card should skip its own "saved" toast (the shared helper already announced
 * "sent for approval"). */
type SaveResult = { pending: boolean } | void;
type SaveFn = (patch: Partial<Employee>) => Promise<SaveResult>;

/** Native select that never loses an unknown current value. */
const SelectField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
  allowEmpty?: string; // label for the empty option
  ariaLabel?: string;
}> = ({ value, onChange, options, disabled, allowEmpty, ariaLabel }) => {
  const known = options.some((o) => o.value === value);
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} className={inputCls} aria-label={ariaLabel}>
      {allowEmpty !== undefined && <option value="">{allowEmpty}</option>}
      {!known && value && <option value={value}>{value}</option>}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
};

/** Edit / Save / Cancel footer used by every editable card. */
const EditFooter: React.FC<{
  editing: boolean;
  saving: boolean;
  dirty: boolean;
  canEdit: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  saveLabel: string;
}> = ({ editing, saving, dirty, canEdit, onEdit, onSave, onCancel, saveLabel }) => {
  const { t } = useTranslation('employees');
  if (!canEdit) return null;
  return (
    <div className="mt-4 flex items-center gap-2">
      {editing ? (
        <>
          <Button onClick={onSave} disabled={!dirty || saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            {saveLabel}
          </Button>
          <Button variant="outline" onClick={onCancel} disabled={saving}>
            <X className="w-4 h-4 mr-1" /> {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
        </>
      ) : (
        <Button variant="outline" onClick={onEdit}>
          <Pencil className="w-4 h-4 mr-1" /> {t('common.edit', { defaultValue: 'Edit' })}
        </Button>
      )}
    </div>
  );
};

const s = (v: unknown): string => (v == null ? '' : String(v));
/** '' → null for optional backend fields. */
const orNull = (v: string): string | null => (v.trim() ? v.trim() : null);

// Module-level field wrappers (stable identity — defining these inside a card
// would remount the input on every render and drop focus per keystroke).
const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string }> = ({ label, children, hint }) => (
  <div className="space-y-1">
    <label className={labelCls}>{label}</label>
    {children}
    {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);
const TextField: React.FC<{
  label: string; value: string; onChange: (v: string) => void; disabled?: boolean; placeholder?: string; type?: string;
}> = ({ label, value, onChange, disabled, placeholder, type = 'text' }) => (
  <Field label={label}>
    <input type={type} className={inputCls} value={value} onChange={(e) => onChange(e.target.value)}
      disabled={disabled} placeholder={placeholder} aria-label={label} />
  </Field>
);

// ---------------------------------------------------------------- Personal
const PersonalCard: React.FC<{ employee: Employee; canEdit: boolean; onSave: SaveFn }> = ({ employee, canEdit, onSave }) => {
  const { t } = useTranslation('employees');
  const initial = () => ({
    first_name: s(employee.first_name),
    middle_name: s(employee.middle_name),
    last_name: s(employee.last_name),
    preferred_name: s(employee.preferred_name),
    gender: s(employee.gender),
    dob: s(employee.dob).slice(0, 10),
    marital_status: s(employee.marital_status),
    nationality: s(employee.nationality),
    phone: s(employee.phone),
    alternate_phone: s(employee.alternate_phone),
    personal_email: s(employee.personal_email),
    email: s(employee.email),
  });
  const [form, setForm] = useState(initial);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(initial()); setEditing(false); }, [employee]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = JSON.stringify(form) !== JSON.stringify(initial());
  const set = (k: keyof ReturnType<typeof initial>, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error(t('detail.name_required', { defaultValue: 'First name and last name are required.' }));
      return;
    }
    if (form.phone.trim() && !isValidPhone(form.phone)) {
      toast.error(t('detail.phone_invalid', { defaultValue: PHONE_HINT }));
      return;
    }
    if (form.alternate_phone.trim() && !isValidPhone(form.alternate_phone)) {
      toast.error(t('detail.phone_invalid', { defaultValue: PHONE_HINT }));
      return;
    }
    // The work email is where payslips and password resets go. A malformed one
    // fails silently — nothing bounces back to HR — so it is refused here
    // rather than saved and discovered when somebody stops being reachable.
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error(t('detail.work_email_invalid', {
        defaultValue: 'That work email is not a valid address.',
      }));
      return;
    }
    setSaving(true);
    try {
      const res = await onSave({
        first_name: form.first_name.trim(),
        middle_name: orNull(form.middle_name),
        last_name: form.last_name.trim(),
        preferred_name: orNull(form.preferred_name),
        gender: orNull(form.gender),
        dob: orNull(form.dob),
        marital_status: orNull(form.marital_status),
        nationality: orNull(form.nationality),
        phone: orNull(form.phone),
        alternate_phone: orNull(form.alternate_phone),
        personal_email: orNull(form.personal_email),
        // Omitted when blank rather than sent as null: a work email is
        // CORRECTED, never cleared — an employee with no work address stops
        // receiving payslips and cannot reset their own password.
        ...(form.email.trim() ? { email: form.email.trim() } : {}),
      });
      setEditing(false);
      if (!res?.pending) toast.success(t('detail.personal_updated', { defaultValue: 'Personal details updated.' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('detail.update_failed')));
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-orange-100 dark:border-orange-500/20">
      <CardHeader>
        <CardTitle className="text-base text-orange-700 flex items-center gap-2">
          <User className="w-4 h-4" /> {t('detail.personal_details', { defaultValue: 'Personal details' })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Editable so a typo can be CORRECTED rather than worked around by
              creating a second person — which is how the duplicates in the
              directory got there. Read-only until Edit is pressed, and shown as
              a mailto link then so it stays one tap to write to somebody. */}
          {editing ? (
            <div>
              <TextField label={t('detail.work_email', { defaultValue: 'Work email' })}
                value={form.email} onChange={(v) => set('email', v)} disabled={!editing} />
              <p className="text-xs text-muted-foreground mt-1">
                {t('detail.work_email_hint', {
                  defaultValue: 'Payslips, notifications and password resets go here.',
                })}
              </p>
            </div>
          ) : (
            <Field label={t('detail.work_email', { defaultValue: 'Work email' })}>
              {employee.email
                ? <a href={`mailto:${employee.email}`} className="block text-sm text-orange-600 hover:underline pt-2 break-words">{employee.email}</a>
                : <p className="text-sm text-muted-foreground pt-2">—</p>}
            </Field>
          )}
          <TextField label={`${t('detail.first_name', { defaultValue: 'First name' })} *`}
            value={form.first_name} onChange={(v) => set('first_name', v)} disabled={!editing} />
          <TextField label={t('detail.middle_name', { defaultValue: 'Middle name' })}
            value={form.middle_name} onChange={(v) => set('middle_name', v)} disabled={!editing} />
          <TextField label={`${t('detail.last_name', { defaultValue: 'Last name' })} *`}
            value={form.last_name} onChange={(v) => set('last_name', v)} disabled={!editing} />
          <TextField label={t('detail.preferred_name', { defaultValue: 'Preferred name' })}
            value={form.preferred_name} onChange={(v) => set('preferred_name', v)} disabled={!editing} />
          <Field label={t('detail.gender', { defaultValue: 'Gender' })}>
            <SelectField value={form.gender} onChange={(v) => set('gender', v)} disabled={!editing}
              ariaLabel={t('detail.gender', { defaultValue: 'Gender' })}
              allowEmpty={t('detail.not_set', { defaultValue: 'Not set' })}
              options={[
                { value: 'male', label: t('detail.gender_male', { defaultValue: 'Male' }) },
                { value: 'female', label: t('detail.gender_female', { defaultValue: 'Female' }) },
                { value: 'other', label: t('detail.gender_other', { defaultValue: 'Other' }) },
                { value: 'prefer_not_to_say', label: t('detail.gender_na', { defaultValue: 'Prefer not to say' }) },
              ]} />
          </Field>
          <TextField type="date" label={t('detail.dob', { defaultValue: 'Date of birth' })}
            value={form.dob} onChange={(v) => set('dob', v)} disabled={!editing} />
          <Field label={t('detail.marital_status', { defaultValue: 'Marital status' })}>
            <SelectField value={form.marital_status} onChange={(v) => set('marital_status', v)} disabled={!editing}
              ariaLabel={t('detail.marital_status', { defaultValue: 'Marital status' })}
              allowEmpty={t('detail.not_set', { defaultValue: 'Not set' })}
              options={[
                { value: 'Single', label: t('detail.marital_single', { defaultValue: 'Single' }) },
                { value: 'Married', label: t('detail.marital_married', { defaultValue: 'Married' }) },
                { value: 'Divorced', label: t('detail.marital_divorced', { defaultValue: 'Divorced' }) },
                { value: 'Widowed', label: t('detail.marital_widowed', { defaultValue: 'Widowed' }) },
              ]} />
          </Field>
          <TextField label={t('detail.nationality', { defaultValue: 'Nationality' })}
            value={form.nationality} onChange={(v) => set('nationality', v)} disabled={!editing} />
          <Field label={t('phone', { defaultValue: 'Phone' })}>
            <PhoneInput value={form.phone} onChange={(v) => set('phone', v)} disabled={!editing} />
          </Field>
          <Field label={t('detail.alternate_phone', { defaultValue: 'Alternate phone' })}>
            <PhoneInput value={form.alternate_phone} onChange={(v) => set('alternate_phone', v)} disabled={!editing} />
          </Field>
          <TextField type="email" label={t('detail.personal_email', { defaultValue: 'Personal email' })}
            value={form.personal_email} onChange={(v) => set('personal_email', v)} disabled={!editing} />
        </div>
        <EditFooter editing={editing} saving={saving} dirty={dirty} canEdit={canEdit}
          onEdit={() => setEditing(true)} onSave={save} onCancel={() => { setForm(initial()); setEditing(false); }}
          saveLabel={t('detail.save_personal', { defaultValue: 'Save personal details' })} />
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------- Identity & IDs
const IdentityCard: React.FC<{ employee: Employee; canEdit: boolean; onSave: SaveFn }> = ({ employee, canEdit, onSave }) => {
  const { t } = useTranslation('employees');
  const initial = () => ({
    employee_id: s(employee.employee_id),
    national_id: s(employee.national_id),
    nssf: s(employee.nssf),
    tin: s(employee.tin),
    wcf_number: s(employee.wcf_number),
    passport_number: s(employee.passport_number),
    work_permit_number: s(employee.work_permit_number),
    resident_permit_number: s(employee.resident_permit_number),
    driver_license: s(employee.driver_license),
    identity_verification_status: s(employee.identity_verification_status),
    scanner_employee_id: s(employee.scanner_employee_id),
    badge_number: s(employee.badge_number),
    legacy_employee_id: s(employee.legacy_employee_id),
  });
  const [form, setForm] = useState(initial);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(initial()); setEditing(false); }, [employee]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = JSON.stringify(form) !== JSON.stringify(initial());
  const set = (k: keyof ReturnType<typeof initial>, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    // Validate the company employee ID + TZ statutory identifiers (unchanged behavior).
    if (!form.employee_id.trim()) { toast.error(t('detail.employee_id_required', { defaultValue: 'Employee ID is required.' })); return; }
    if (!isValidNationalId(form.national_id)) { toast.error(NATIONAL_ID_HINT); return; }
    if (!isValidNSSF(form.nssf)) { toast.error(NSSF_HINT); return; }
    if (!isValidTIN(form.tin)) { toast.error(TIN_HINT); return; }
    setSaving(true);
    try {
      const res = await onSave({
        employee_id: form.employee_id.trim(),
        national_id: orNull(form.national_id),
        nssf: orNull(form.nssf),
        tin: orNull(form.tin),
        wcf_number: orNull(form.wcf_number),
        passport_number: orNull(form.passport_number),
        work_permit_number: orNull(form.work_permit_number),
        resident_permit_number: orNull(form.resident_permit_number),
        driver_license: orNull(form.driver_license),
        identity_verification_status: orNull(form.identity_verification_status),
        scanner_employee_id: orNull(form.scanner_employee_id),
        badge_number: orNull(form.badge_number),
        legacy_employee_id: orNull(form.legacy_employee_id),
      });
      setEditing(false);
      if (!res?.pending) toast.success(t('detail.identification_updated', { defaultValue: 'Identification updated.' }));
    } catch (e: any) {
      // Surface the backend's clean message (e.g. duplicate Employee ID).
      toast.error(getApiErrorMessage(e, t('detail.update_failed')));
    } finally { setSaving(false); }
  };

  // Bound text field for this card's form keys (plain function returning JSX,
  // NOT a nested component — keeps input identity stable across renders).
  const txt = (k: keyof ReturnType<typeof initial>, label: string, placeholder?: string) => (
    <TextField label={label} value={form[k]} onChange={(v) => set(k, v)} disabled={!editing} placeholder={placeholder} />
  );

  return (
    <Card className="border-orange-100 dark:border-orange-500/20">
      <CardHeader>
        <CardTitle className="text-base text-orange-700 flex items-center gap-2">
          <Hash className="w-4 h-4" /> {t('detail.identification', { defaultValue: 'Identification' })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {txt('employee_id', t('detail.employee_id', { defaultValue: 'Employee ID (company number)' }), t('detail.employee_id_ph', { defaultValue: 'e.g. EMP-001' }))}
          {txt('national_id', t('detail.national_id', { defaultValue: 'National ID Number' }))}
          {txt('nssf', t('detail.nssf', { defaultValue: 'NSSF Number' }))}
          {txt('tin', t('detail.tin', { defaultValue: 'TIN Number' }))}
          {txt('wcf_number', t('detail.wcf_number', { defaultValue: 'WCF Number' }))}
          {txt('passport_number', t('detail.passport_number', { defaultValue: 'Passport Number' }))}
          {txt('work_permit_number', t('detail.work_permit', { defaultValue: 'Work Permit Number' }))}
          {txt('resident_permit_number', t('detail.resident_permit', { defaultValue: 'Resident Permit Number' }))}
          {txt('driver_license', t('detail.driver_license', { defaultValue: 'Driver License' }))}
          <Field label={t('detail.id_verification', { defaultValue: 'Identity verification status' })}>
            <SelectField value={form.identity_verification_status} onChange={(v) => set('identity_verification_status', v)} disabled={!editing}
              ariaLabel={t('detail.id_verification', { defaultValue: 'Identity verification status' })}
              allowEmpty={t('detail.not_set', { defaultValue: 'Not set' })}
              options={[
                { value: 'missing', label: t('detail.idv_missing', { defaultValue: 'Missing' }) },
                { value: 'pending', label: t('detail.idv_pending', { defaultValue: 'Pending' }) },
                { value: 'verified', label: t('detail.idv_verified', { defaultValue: 'Verified' }) },
                { value: 'rejected', label: t('detail.idv_rejected', { defaultValue: 'Rejected' }) },
                { value: 'not_applicable', label: t('detail.idv_na', { defaultValue: 'Not applicable' }) },
              ]} />
          </Field>
        </div>

        {/* Attendance mapping — how this person appears in scanner/clock exports */}
        <div className="mt-6 border-t border-border pt-4">
          <p className="text-sm font-medium text-foreground mb-3 flex items-center gap-1.5">
            <BadgeCheck className="w-4 h-4 text-orange-500" /> {t('detail.attendance_mapping', { defaultValue: 'Attendance mapping' })}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {txt('scanner_employee_id', t('detail.scanner_id', { defaultValue: 'Scanner employee ID' }), t('detail.scanner_id_ph', { defaultValue: 'ID used by the attendance device' }))}
            {txt('badge_number', t('detail.badge_number', { defaultValue: 'Badge number' }))}
            {txt('legacy_employee_id', t('detail.legacy_id', { defaultValue: 'Legacy employee ID' }))}
          </div>
        </div>

        <EditFooter editing={editing} saving={saving} dirty={dirty} canEdit={canEdit}
          onEdit={() => setEditing(true)} onSave={save} onCancel={() => { setForm(initial()); setEditing(false); }}
          saveLabel={t('detail.save_identification', { defaultValue: 'Save identification' })} />
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------- Employment
const STATUS_OPTIONS = ['Active', 'Draft', 'Suspended', 'Inactive', 'Terminated', 'Archived'];

const EmploymentCard: React.FC<{ employee: Employee; canEdit: boolean; onSave: SaveFn }> = ({ employee, canEdit, onSave }) => {
  const { t } = useTranslation('employees');
  const initial = () => ({
    employment_type: s(employee.employment_type),
    worker_classification: s(employee.worker_classification),
    work_schedule: s(employee.work_schedule),
    contract_type: s(employee.contract_type),
    status: s(employee.status),
    hire_date: s(employee.hire_date).slice(0, 10),
    confirmation_date: s(employee.confirmation_date).slice(0, 10),
    probation_end_date: s(employee.probation_end_date).slice(0, 10),
    contract_start_date: s(employee.contract_start_date).slice(0, 10),
    contract_end_date: s(employee.contract_end_date).slice(0, 10),
    termination_date: s(employee.termination_date).slice(0, 10),
    termination_reason: s(employee.termination_reason),
    rehire_eligible: !!employee.rehire_eligible,
  });
  const [form, setForm] = useState(initial);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(initial()); setEditing(false); }, [employee]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = JSON.stringify(form) !== JSON.stringify(initial());
  const set = <K extends keyof ReturnType<typeof initial>>(k: K, v: ReturnType<typeof initial>[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const isTerminated = form.status.toLowerCase() === 'terminated';

  const save = async () => {
    // Fixed-term contracts must carry an end date (statutory: the "specified
    // period"). Strong client-side guard; the WCF category still derives from
    // contract_type regardless.
    if (form.contract_type === 'fixed_term' && !form.contract_end_date) {
      toast.error(t('detail.fixed_term_needs_end', { defaultValue: 'A fixed-term contract needs a contract end date.' }));
      return;
    }
    setSaving(true);
    try {
      const res = await onSave({
        employment_type: orNull(form.employment_type),
        worker_classification: orNull(form.worker_classification),
        work_schedule: orNull(form.work_schedule),
        contract_type: orNull(form.contract_type),
        ...(form.status ? { status: form.status } : {}),
        ...(form.hire_date ? { hire_date: form.hire_date } : {}),
        confirmation_date: orNull(form.confirmation_date),
        probation_end_date: orNull(form.probation_end_date),
        contract_start_date: orNull(form.contract_start_date),
        contract_end_date: orNull(form.contract_end_date),
        ...(isTerminated
          ? {
              termination_date: orNull(form.termination_date),
              termination_reason: orNull(form.termination_reason),
              rehire_eligible: form.rehire_eligible,
            }
          : {}),
      });
      setEditing(false);
      if (!res?.pending) toast.success(t('detail.employment_updated', { defaultValue: 'Employment details updated.' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('detail.update_failed')));
    } finally { setSaving(false); }
  };

  // Plain function returning JSX (not a nested component) — stable input identity.
  const dateField = (k: 'hire_date' | 'confirmation_date' | 'probation_end_date' | 'contract_start_date' | 'contract_end_date' | 'termination_date', label: string) => (
    <TextField type="date" label={label} value={form[k]} onChange={(v) => set(k, v)} disabled={!editing} />
  );

  const employmentTypeOptions = [
    { value: 'full_time', label: t('detail.et_full_time', { defaultValue: 'Full-time' }) },
    { value: 'part_time', label: t('detail.et_part_time', { defaultValue: 'Part-time' }) },
    { value: 'casual', label: t('detail.et_casual', { defaultValue: 'Casual' }) },
    { value: 'contract', label: t('detail.et_contract', { defaultValue: 'Contract' }) },
    { value: 'fixed_term', label: t('detail.et_fixed_term', { defaultValue: 'Fixed term' }) },
    { value: 'specific_task', label: t('detail.et_specific_task', { defaultValue: 'Specific task (ELRA)' }) },
    { value: 'intern', label: t('detail.et_intern', { defaultValue: 'Intern' }) },
    { value: 'seasonal', label: t('detail.et_seasonal', { defaultValue: 'Seasonal' }) },
    { value: 'consultant', label: t('detail.et_consultant', { defaultValue: 'Consultant' }) },
    { value: 'temporary', label: t('detail.et_temporary', { defaultValue: 'Temporary' }) },
    { value: 'other', label: t('detail.et_other', { defaultValue: 'Other' }) },
  ];
  const classificationOptions = [
    { value: 'employee', label: t('detail.wc_employee', { defaultValue: 'Employee' }) },
    { value: 'contractor', label: t('detail.wc_contractor', { defaultValue: 'Contractor' }) },
    { value: 'casual_worker', label: t('detail.wc_casual', { defaultValue: 'Casual worker' }) },
    { value: 'temporary_worker', label: t('detail.wc_temporary', { defaultValue: 'Temporary worker' }) },
  ];
  const workScheduleOptions = [
    { value: 'full_time', label: t('detail.ws_full_time', { defaultValue: 'Full time' }) },
    { value: 'part_time', label: t('detail.ws_part_time', { defaultValue: 'Part time' }) },
  ];
  const contractTypeOptions = [
    { value: 'permanent', label: t('detail.ct_permanent', { defaultValue: 'Permanent' }) },
    { value: 'fixed_term', label: t('detail.ct_fixed_term', { defaultValue: 'Fixed term' }) },
    { value: 'specific_task', label: t('detail.ct_specific_task', { defaultValue: 'Specific task (ELRA)' }) },
  ];

  return (
    <Card className="border-orange-100 dark:border-orange-500/20">
      <CardHeader>
        <CardTitle className="text-base text-orange-700 flex items-center gap-2">
          <Briefcase className="w-4 h-4" /> {t('detail.employment', { defaultValue: 'Employment' })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label={t('detail.employment_type', { defaultValue: 'Employment type' })}>
            <SelectField value={form.employment_type} onChange={(v) => set('employment_type', v)} disabled={!editing}
              ariaLabel={t('detail.employment_type', { defaultValue: 'Employment type' })}
              allowEmpty={t('detail.not_set', { defaultValue: 'Not set' })} options={employmentTypeOptions} />
          </Field>
          <Field label={t('detail.worker_classification', { defaultValue: 'Worker classification' })}>
            <SelectField value={form.worker_classification} onChange={(v) => set('worker_classification', v)} disabled={!editing}
              ariaLabel={t('detail.worker_classification', { defaultValue: 'Worker classification' })}
              allowEmpty={t('detail.not_set', { defaultValue: 'Not set' })} options={classificationOptions} />
          </Field>
          <Field label={t('detail.work_schedule', { defaultValue: 'Work schedule' })}>
            <SelectField value={form.work_schedule} onChange={(v) => set('work_schedule', v)} disabled={!editing}
              ariaLabel={t('detail.work_schedule', { defaultValue: 'Work schedule' })}
              allowEmpty={t('detail.not_set', { defaultValue: 'Not set' })} options={workScheduleOptions} />
          </Field>
          <Field label={t('detail.contract_type', { defaultValue: 'Contract type' })}
            hint={t('detail.contract_type_hint', { defaultValue: 'Determines the statutory (WCF) employment category. Not the same as work schedule.' })}>
            <SelectField value={form.contract_type} onChange={(v) => set('contract_type', v)} disabled={!editing}
              ariaLabel={t('detail.contract_type', { defaultValue: 'Contract type' })}
              allowEmpty={t('detail.not_set', { defaultValue: 'Not set' })} options={contractTypeOptions} />
          </Field>
          <Field label={t('detail.status', { defaultValue: 'Status' })}>
            <SelectField value={form.status} onChange={(v) => set('status', v)} disabled={!editing}
              ariaLabel={t('detail.status', { defaultValue: 'Status' })}
              options={STATUS_OPTIONS.map((v) => ({ value: v, label: v }))} />
          </Field>
          {dateField('hire_date', t('hire_date', { defaultValue: 'Hire date' }))}
          {dateField('confirmation_date', t('detail.confirmation_date', { defaultValue: 'Confirmation date' }))}
          {dateField('probation_end_date', t('detail.probation_end', { defaultValue: 'Probation end date' }))}
          {dateField('contract_start_date', t('detail.contract_start', { defaultValue: 'Contract start date' }))}
          {dateField('contract_end_date', t('detail.contract_end', { defaultValue: 'Contract end date' }))}
        </div>

        {isTerminated && (
          <div className="mt-6 border-t border-border pt-4">
            <p className="text-sm font-medium text-foreground mb-3">
              {t('detail.termination', { defaultValue: 'Termination' })}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {dateField('termination_date', t('detail.termination_date', { defaultValue: 'Termination date' }))}
              <div className="space-y-1 md:col-span-2">
                <label className={labelCls}>{t('detail.termination_reason', { defaultValue: 'Termination reason' })}</label>
                <Textarea value={form.termination_reason} onChange={(e) => set('termination_reason', e.target.value)} disabled={!editing} className="min-h-[40px]" />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <Switch checked={form.rehire_eligible} onCheckedChange={(v) => set('rehire_eligible', v)} disabled={!editing}
                aria-label={t('detail.rehire_eligible', { defaultValue: 'Eligible for rehire' })} />
              <span className="text-sm text-foreground">{t('detail.rehire_eligible', { defaultValue: 'Eligible for rehire' })}</span>
            </div>
          </div>
        )}

        <EditFooter editing={editing} saving={saving} dirty={dirty} canEdit={canEdit}
          onEdit={() => setEditing(true)} onSave={save} onCancel={() => { setForm(initial()); setEditing(false); }}
          saveLabel={t('detail.save_employment', { defaultValue: 'Save employment' })} />
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------- Work assignment (branch etc.)
const WorkAssignmentCard: React.FC<{ employee: Employee; canEdit: boolean; onSave: SaveFn }> = ({ employee, canEdit, onSave }) => {
  const { t } = useTranslation('employees');
  const [branches, setBranches] = useState<Branch[]>([]);
  const initial = () => ({
    branch_id: s(employee.branch_id),
    cost_center: s(employee.cost_center),
    project: s(employee.project),
    work_location_type: s(employee.work_location_type),
  });
  const [form, setForm] = useState(initial);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addingBranch, setAddingBranch] = useState(false);
  const [newBranchName, setNewBranchName] = useState('');
  const [creatingBranch, setCreatingBranch] = useState(false);

  useEffect(() => { setForm(initial()); setEditing(false); }, [employee]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { listBranches().then(setBranches).catch(() => setBranches([])); }, []);

  const dirty = JSON.stringify(form) !== JSON.stringify(initial());
  const set = (k: keyof ReturnType<typeof initial>, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const addBranch = async () => {
    if (!newBranchName.trim()) return;
    setCreatingBranch(true);
    try {
      const b = await createBranch({ name: newBranchName.trim() });
      setBranches((cur) => [...cur, b]);
      set('branch_id', b.id);
      setNewBranchName('');
      setAddingBranch(false);
      toast.success(t('detail.branch_created', { defaultValue: 'Branch created.' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('detail.branch_create_failed', { defaultValue: 'Could not create branch.' })));
    } finally { setCreatingBranch(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await onSave({
        branch_id: orNull(form.branch_id),
        cost_center: orNull(form.cost_center),
        project: orNull(form.project),
        work_location_type: orNull(form.work_location_type),
      });
      setEditing(false);
      if (!res?.pending) toast.success(t('detail.assignment_updated', { defaultValue: 'Work assignment updated.' }));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('detail.update_failed')));
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-orange-100 dark:border-orange-500/20">
      <CardHeader>
        <CardTitle className="text-base text-orange-700 flex items-center gap-2">
          <MapPin className="w-4 h-4" /> {t('detail.work_assignment', { defaultValue: 'Work assignment' })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className={labelCls}>{t('detail.branch', { defaultValue: 'Branch' })}</label>
            <SelectField value={form.branch_id} onChange={(v) => set('branch_id', v)} disabled={!editing}
              ariaLabel={t('detail.branch', { defaultValue: 'Branch' })}
              allowEmpty={t('detail.unassigned_option')}
              options={branches
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((b) => ({ value: b.id, label: b.code ? `${b.name} (${b.code})` : b.name }))} />
            {canEdit && editing && (
              addingBranch ? (
                <div className="flex items-center gap-2 pt-1">
                  <input className={inputCls} value={newBranchName} onChange={(e) => setNewBranchName(e.target.value)}
                    placeholder={t('detail.branch_name_ph', { defaultValue: 'New branch name' })} />
                  <Button size="sm" onClick={addBranch} disabled={creatingBranch || !newBranchName.trim()}>
                    {creatingBranch ? <Loader2 className="w-4 h-4 animate-spin" /> : t('detail.add', { defaultValue: 'Add' })}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setAddingBranch(false); setNewBranchName(''); }} disabled={creatingBranch}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <button type="button" onClick={() => setAddingBranch(true)}
                  className="flex items-center gap-1 text-xs text-orange-700 hover:underline pt-1">
                  <Plus className="w-3 h-3" /> {t('detail.add_branch', { defaultValue: 'Add branch' })}
                </button>
              )
            )}
          </div>
          <div className="space-y-1">
            <label className={labelCls}>{t('detail.work_location_type', { defaultValue: 'Work location type' })}</label>
            <SelectField value={form.work_location_type} onChange={(v) => set('work_location_type', v)} disabled={!editing}
              ariaLabel={t('detail.work_location_type', { defaultValue: 'Work location type' })}
              allowEmpty={t('detail.not_set', { defaultValue: 'Not set' })}
              options={[
                { value: 'office', label: t('detail.wl_office', { defaultValue: 'Office' }) },
                { value: 'field', label: t('detail.wl_field', { defaultValue: 'Field' }) },
                { value: 'remote', label: t('detail.wl_remote', { defaultValue: 'Remote' }) },
                { value: 'hybrid', label: t('detail.wl_hybrid', { defaultValue: 'Hybrid' }) },
                { value: 'client_site', label: t('detail.wl_client_site', { defaultValue: 'Client site' }) },
              ]} />
          </div>
          <div className="space-y-1">
            <label className={labelCls}>{t('detail.cost_center', { defaultValue: 'Cost center' })}</label>
            <input className={inputCls} value={form.cost_center} onChange={(e) => set('cost_center', e.target.value)} disabled={!editing} />
          </div>
          <div className="space-y-1">
            <label className={labelCls}>{t('detail.project', { defaultValue: 'Project' })}</label>
            <input className={inputCls} value={form.project} onChange={(e) => set('project', e.target.value)} disabled={!editing} />
          </div>
        </div>
        <EditFooter editing={editing} saving={saving} dirty={dirty} canEdit={canEdit}
          onEdit={() => setEditing(true)} onSave={save} onCancel={() => { setForm(initial()); setEditing(false); }}
          saveLabel={t('detail.save_assignment', { defaultValue: 'Save work assignment' })} />
      </CardContent>
    </Card>
  );
};

// ============================================================================
export const EmployeeDetailPage: React.FC = () => {
  const { t } = useTranslation('employees');
  const { employeeId } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Editing others' org placement / master data requires employee:write
  // (matches the backend).
  const canOrganize = useHasPermission('employee:write');
  // Salary split (Increment B): payroll staff propose, payroll approvers decide.
  const canProposeSplit = useHasPermission('payroll:write');
  const canApproveSplit = useHasPermission('payroll:approve');
  // Salary is sensitive — HR managers and owners only.
  const { isTenantOwner, isHrManager, canRunPayroll, isManager, canManageEmployees } = useRoleAccess();
  const canManagePay = isTenantOwner || isHrManager;
  // THIS PAGE IS THE HR VIEW OF SOMEBODY'S RECORD, and it has never known when
  // that somebody is you. Without this, opening your own row from the directory
  // silently shows fewer tabs and no Edit button, and reads as "I am not allowed
  // to change my own phone number" — which is false: My Profile has the full
  // self-service form, including the change-request flow for fields HR approves.
  //
  // auth.user.id is safe to compare: authSlice reconciles it against the session
  // on every hydration (the /me split-brain fix), so it is the signed-in
  // principal, not a stale persisted render hint.
  const myUserId = useAppSelector((st) => st.auth?.user?.id) ?? null;

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [all, setAll] = useState<Employee[]>([]);
  const [deptName, setDeptName] = useState<string>('');
  const [leave, setLeave] = useState<LeavePeriod[]>([]);
  const [summary, setSummary] = useState<OrgSummary | null>(null);
  const [readiness, setReadiness] = useState<EmployeeReadiness | null>(null);
  // In-progress onboarding/offboarding checklist (Lifecycle L2). The tab only
  // renders while an instance is running; null hides it entirely.
  const [lifecycle, setLifecycle] = useState<LifecycleInstanceView | null>(null);
  // Bumped after every document upload/delete so the required-documents
  // checklist refetches its status.
  const [docsVersion, setDocsVersion] = useState(0);
  // Active profile tab (controlled) so a readiness "Fix" can jump to the editor.
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // HTTP status of a load failure, so the empty state can theme a 403 (no access) apart
  // from a genuine error.
  const [errorStatus, setErrorStatus] = useState<number | null>(null);

  // Inline "organize" editor (admins/managers): set manager + org level +
  // department + job title.
  const [editLevel, setEditLevel] = useState<string>('employee');
  const [editManager, setEditManager] = useState<string>('');
  // When a NEW manager takes over. Empty = today; a future date plans the
  // move and the current manager keeps the person until then.
  const [editManagerFrom, setEditManagerFrom] = useState<string>('');
  const [editDept, setEditDept] = useState<string>('');
  const [editTitle, setEditTitle] = useState<string>('');
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
  const [titleOptions, setTitleOptions] = useState<string[]>([]);
  const [savingOrg, setSavingOrg] = useState(false);
  // Read-only until Edit is clicked — org placement can't change by accident.
  const [orgEdit, setOrgEdit] = useState(false);
  // Danger-zone delete (duplicates only). Typed-name confirmation.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  // Retire the linked sign-in account along with a duplicate record. Default
  // OFF: it disables a real person's account, so it is always a deliberate tick.
  const [retireLogin, setRetireLogin] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const resetOrg = () => {
    setEditLevel(employee?.org_level || 'employee');
    setEditManager(employee?.manager_id || '');
    setEditManagerFrom('');
    setEditDept(employee?.department_id || '');
    setEditTitle(employee?.position || '');
  };

  // Resolve the directory base so links work under both /dashboard/:slug and
  // /t/:slug/app route structures.
  const employeesBase = useMemo(
    () => location.pathname.replace(/\/employees(\/.*)?$/, '/employees'),
    [location.pathname],
  );
  const goToEmployee = (id: string) => navigate(`${employeesBase}/${id}`);

  // My Profile lives beside the directory under the same tenant base.
  const myProfileBase = useMemo(
    () => location.pathname.replace(/\/employees(\/.*)?$/, '/personal'),
    [location.pathname],
  );

  const refreshReadiness = (id: string) => {
    getEmployeeReadiness(id).then(setReadiness).catch(() => setReadiness(null));
  };
  // Best-effort: 403/404 (or no instance) simply hides the lifecycle tab.
  const refreshLifecycle = (id: string) => {
    getEmployeeLifecycle(id).then(setLifecycle).catch(() => setLifecycle(null));
  };

  useEffect(() => {
    // Dana is a virtual record — no backend fetch (see DanaProfile).
    if (!employeeId || isDanaId(employeeId)) return;
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const [emp, everyone, deptRes, out, orgSummary, titleRes] = await Promise.all([
          getEmployee(employeeId),
          getAllEmployees().catch(() => [] as Employee[]),
          getDepartments().then((d) => d.items || []).catch(() => []),
          getTeamOut(90).catch(() => [] as any[]),
          getOrgSummary(employeeId).catch(() => null),
          getJobTitles().then((r) => r.items || []).catch(() => []),
        ]);
        if (!active) return;
        setEmployee(emp);
        setAll(everyone);
        setSummary(orgSummary);
        setEditLevel(emp.org_level || 'employee');
        setEditManager(emp.manager_id || '');
        setEditDept(emp.department_id || '');
        setEditTitle(emp.position || '');
        setDepartments((deptRes as any[]).map((d) => ({ id: d.id, name: d.name })));
        setTitleOptions(Array.from(new Set((titleRes as any[]).map((tt) => tt.name).filter(Boolean))) as string[]);
        const dmap = new Map<string, string>((deptRes as any[]).map((d) => [d.id, d.name]));
        setDeptName((emp.department_id && dmap.get(emp.department_id)) || '');
        // This employee's own approved leave (employee_id on leave == user id).
        setLeave(
          (out as any[])
            .filter((o) => emp.user_id && String(o.employee_id) === String(emp.user_id))
            .map((o) => ({ type: o.type, start_date: o.start_date, end_date: o.end_date })),
        );
        // Best-effort readiness — hidden if the endpoint isn't reachable.
        refreshReadiness(employeeId);
        refreshLifecycle(employeeId);
      } catch (e: any) {
        if (active) {
          // Surface the backend's own reason (e.g. a 403 "Permission 'employee:read'
          // required") so it's clear WHY — a permissions problem is not a load failure and
          // retrying won't help. Keep the friendly 404 copy since a not-found body isn't
          // user-meaningful.
          const st = e?.response?.status ?? null;
          setErrorStatus(st);
          setError(
            st === 404
              ? t('detail.not_found')
              : getApiErrorMessage(e, t('detail.load_error')),
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [employeeId]);

  const fullName = employee ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || employee.email : '';
  const initials = (name: string) => name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

  // When the tenant's approval policy gates the touched fields, the backend
  // answers 202 with a pending-approval envelope instead of the employee.
  // Announce it and refetch (never trust the envelope as employee state).
  const handlePendingApproval = async (fields: string[]): Promise<{ pending: true }> => {
    toast(
      t('detail.sent_for_approval', {
        defaultValue: 'Sent for approval: {{fields}}',
        fields: fields.map(humanizeField).join(', '),
      }),
      { icon: '⏳', duration: 6000 },
    );
    if (employee) {
      // Refetch so any optimistic/stale values are discarded.
      try { setEmployee(await getEmployee(employee.id)); } catch { /* keep current */ }
    }
    return { pending: true };
  };

  // Shared save path for every master-data card: PUT any subset, keep the
  // page's employee object fresh, and recompute readiness. A 202 (maker-
  // checker) response is surfaced as `pending` so cards skip their own toast.
  const applyUpdate: SaveFn = async (patch) => {
    if (!employee) return;
    const updated = await updateEmployee(employee.id, patch as any);
    if (isPendingApproval(updated)) return handlePendingApproval(updated.fields);
    setEmployee(updated);
    refreshReadiness(employee.id);
  };

  const saveOrg = async () => {
    if (!employee) return;
    setSavingOrg(true);
    try {
      const managerChanging = (editManager || '') !== (employee.manager_id || '');
      const plannedFrom = managerChanging && editManagerFrom && editManagerFrom > localTodayIso()
        ? editManagerFrom : '';
      const updated = await updateEmployee(employee.id, {
        org_level: editLevel,
        // Empty selection clears the manager (top of chain).
        manager_id: editManager || null,
        ...(managerChanging && editManagerFrom ? { manager_effective_date: editManagerFrom } : {}),
        department_id: editDept || null,
        // Title is required server-side (min length 1); only send when non-empty.
        ...(editTitle.trim() ? { position: editTitle.trim() } : {}),
      } as any);
      if (isPendingApproval(updated)) {
        await handlePendingApproval(updated.fields);
        setOrgEdit(false);
        resetOrg();
        return;
      }
      setEmployee(updated);
      setOrgEdit(false);
      setDeptName(departments.find((d) => d.id === editDept)?.name || '');
      if (plannedFrom) {
        toast.success(t('detail.manager_planned', {
          defaultValue: 'Planned: the new manager takes over on {{date}}. Until then nothing changes.',
          date: formatDate(plannedFrom) }));
      }
      getOrgSummary(employee.id).then(setSummary).catch(() => {});
      refreshReadiness(employee.id);
      toast.success(t('detail.org_updated'));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('detail.update_failed')));
    } finally {
      setSavingOrg(false);
    }
  };
  const orgDirty = !!employee && (
    editLevel !== (employee.org_level || 'employee')
    || (editManager || '') !== (employee.manager_id || '')
    || (editDept || '') !== (employee.department_id || '')
    || editTitle.trim() !== (employee.position || '')
  );

  const isOutNow = useMemo(() => isOnLeaveOn(leave, localTodayIso()), [leave]);

  const status = useMemo(
    () => resolveEmployeeStatus(employee?.status, isOutNow),
    [employee, isOutNow]
  );

  const manager = employee?.manager_id ? all.find((e) => e.id === employee.manager_id) : undefined;
  const directReports = employee ? all.filter((e) => e.manager_id === employee.id) : [];

  // Anchor civil dates (date-only strings) to local midnight so hire date / DOB /
  // leave dates never shift a day for UTC-negative viewers (US-07).
  const fmt = (d?: string) => (d ? toDisplayDate(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

  // Virtual AI assistant profile — rendered without any backend fetch.
  if (isDanaId(employeeId)) {
    return <DanaProfile backTo={employeesBase} />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> {t('detail.loading')}
      </div>
    );
  }
  if (error || !employee) {
    const forbidden = errorStatus === 403;
    const StateIcon = forbidden ? ShieldAlert : AlertTriangle;
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate(employeesBase)} className="text-orange-700">
          <ArrowLeft className="w-4 h-4 mr-2" /> {t('detail.back')}
        </Button>
        <div className="mx-auto mt-6 max-w-md rounded-2xl border border-border bg-card p-8 text-center">
          <div
            className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${
              forbidden
                ? 'bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400'
                : 'bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400'
            }`}
          >
            <StateIcon className="h-6 w-6" />
          </div>
          <h2 className="mt-4 text-base font-semibold text-charcoal">
            {forbidden
              ? t('detail.no_access_title', { defaultValue: "You don't have access to this profile" })
              : t('detail.load_error_title', { defaultValue: "Couldn't open this profile" })}
          </h2>
          <p className="mt-1.5 text-sm text-muted-foreground">{error || t('detail.not_found')}</p>
          <Button className="mt-5" variant="outline" onClick={() => navigate(employeesBase)}>
            {t('detail.back_to_directory', { defaultValue: 'Back to directory' })}
          </Button>
        </div>
      </div>
    );
  }

  const Row = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) => (
    <div className="flex items-start gap-3">
      <span className="text-orange-500 mt-0.5">{icon}</span>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm text-foreground break-words">{value || '—'}</p>
      </div>
    </div>
  );

  const isSelf = isSelfRecord(myUserId, employee?.user_id);

  const showPayrollTab = canManagePay || canRunPayroll;
  // Change history is an audit surface — managers and HR (employee:write) only.
  const showHistoryTab = isManager || canManageEmployees;
  // Lifecycle task board — only while an onboarding/offboarding is running.
  const showLifecycleTab = !!lifecycle && lifecycle.status === 'in_progress';
  const lifecycleTabLabel = lifecycle?.type === 'offboarding'
    ? t('lifecycle.offboarding', { defaultValue: 'Offboarding' })
    : t('lifecycle.onboarding', { defaultValue: 'Onboarding' });
  // Size each tab to its label (flex-auto), then share the spare room; the list
  // wraps. flex-1 + a fixed min-width squeezed long labels below their own width
  // ("Employment & Engagements" overlapped "Job & Organization").
  const tabTriggerCls = 'flex-auto min-w-[110px]';
  const isDraft = (employee?.status || '').toLowerCase() === 'draft';

  // Readiness "Fix" → jump to the tab that edits the missing field. Falls back to
  // a tab the caller can actually see (gated tabs collapse to Personal).
  const fixField = (code: string, bucket: string) => {
    const FIELD_TAB: Record<string, string> = {
      dob: 'personal', gender: 'personal', phone: 'personal', email: 'personal', emergency_contact: 'personal',
      employment_type: 'employment', hire_date: 'employment', position: 'employment',
      department: 'organization', manager: 'organization',
      national_id: 'identity', tin: 'identity', nssf: 'identity',
      scanner_employee_id: 'identity', badge_number: 'identity',
    };
    let target = FIELD_TAB[code] || (bucket === 'documents' ? 'documents' : 'employment');
    if ((target === 'identity' || target === 'organization') && !canOrganize) target = 'employment';
    if (target === 'documents' && !canOrganize) target = 'overview';
    if (target === 'payroll' && !showPayrollTab) target = 'overview';
    setTab(target);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate(employeesBase)} className="text-orange-700 hover:bg-orange-50 -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> {t('detail.back')}
      </Button>

      {/* Header */}
      <Card className="border-2 border-orange-100 dark:border-orange-500/20">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-center gap-5">
            <Avatar className="w-20 h-20 border-2 border-orange-200 dark:border-orange-500/25">
              <AvatarImage src={employee.profile_picture_url || undefined} alt={fullName} />
              <AvatarFallback className="bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 text-2xl">{initials(fullName)}</AvatarFallback>
            </Avatar>
            <div className="text-center sm:text-left space-y-1">
              <h1 className="text-h3 text-charcoal">
                {fullName}
                {employee.preferred_name ? <span className="text-base font-normal text-muted-foreground"> “{employee.preferred_name}”</span> : null}
              </h1>
              <p className="text-foreground">{employee.position || '—'}{deptName ? ` · ${deptName}` : ''}</p>
              <div className="pt-1 flex items-center justify-center sm:justify-start gap-2 flex-wrap">
                <Badge className={`border ${levelColor(employee.org_level)}`}>{levelLabel(employee.org_level)}</Badge>
                <EmployeeStatusBadge status={status} />
                {isDraft && readiness && (readiness.missing_count ?? 0) > 0 && (
                  <Badge className="border bg-warning-soft text-warning-strong border-transparent">
                    {t('detail.to_activate', { defaultValue: '{{count}} to activate', count: readiness.missing_count })}
                  </Badge>
                )}
                {readiness && <ReadinessBadge readiness={readiness} />}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Viewing your OWN record. The tabs and the Edit button below are gated on
          employee:write — correct for a colleague's record, misleading for your
          own, because it looks like a refusal rather than a different door. Say
          which view this is and point at the one that can actually edit. */}
      {isSelf && !canOrganize && (
        <Card className="border-orange-200 dark:border-orange-500/25 bg-orange-50/60 dark:bg-orange-500/[0.06]">
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <User className="w-4 h-4 mt-0.5 text-orange-500 shrink-0" />
              <p className="text-sm text-foreground">
                {t('detail.self_view', {
                  defaultValue:
                    'This is your own record, shown the way your organisation sees it. To change your details, open My Profile.',
                })}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => navigate(myProfileBase)}
            >
              {t('detail.go_to_my_profile', { defaultValue: 'Go to My Profile' })}
            </Button>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="flex flex-wrap w-full h-auto gap-1 bg-orange-50 dark:bg-orange-500/[0.08] border border-orange-200 dark:border-orange-500/25 p-1">
          <TabsTrigger value="overview" className={tabTriggerCls}>{t('detail.tab_overview', { defaultValue: 'Overview' })}</TabsTrigger>
          {showLifecycleTab && (
            <TabsTrigger value="lifecycle" className={tabTriggerCls}>{lifecycleTabLabel}</TabsTrigger>
          )}
          <TabsTrigger value="personal" className={tabTriggerCls}>{t('detail.tab_personal', { defaultValue: 'Personal' })}</TabsTrigger>
          {canOrganize && (
            <TabsTrigger value="identity" className={tabTriggerCls}>{t('detail.tab_identity', { defaultValue: 'Identity & IDs' })}</TabsTrigger>
          )}
          <TabsTrigger value="employment" className={tabTriggerCls}>{t('detail.tab_employment_engagements', { defaultValue: 'Employment & Engagements' })}</TabsTrigger>
          {canOrganize && (
            <TabsTrigger value="organization" className={tabTriggerCls}>{t('detail.tab_organization', { defaultValue: 'Job & Organization' })}</TabsTrigger>
          )}
          {showPayrollTab && (
            <TabsTrigger value="payroll" className={tabTriggerCls}>{t('detail.tab_payroll', { defaultValue: 'Payroll' })}</TabsTrigger>
          )}
          {canOrganize && (
            <TabsTrigger value="documents" className={tabTriggerCls}>{t('detail.tab_documents', { defaultValue: 'Documents' })}</TabsTrigger>
          )}
          {showHistoryTab && (
            <TabsTrigger value="history" className={tabTriggerCls}>{t('detail.tab_history', { defaultValue: 'History' })}</TabsTrigger>
          )}
        </TabsList>

        {/* -------------------------------------------------- Overview */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contact (quick view — editable in the Personal tab) */}
            <Card className="border-orange-100 dark:border-orange-500/20">
              <CardHeader><CardTitle className="text-base text-orange-700">{t('detail.contact')}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <Row icon={<Mail className="w-4 h-4" />} label={t('email')} value={
                  employee.email ? <a href={`mailto:${employee.email}`} className="text-orange-600 hover:underline">{employee.email}</a> : '—'
                } />
                <Row icon={<Phone className="w-4 h-4" />} label={t('phone')} value={
                  employee.phone ? <a href={`tel:${employee.phone}`} className="text-orange-600 hover:underline">{employee.phone}</a> : '—'
                } />
              </CardContent>
            </Card>

            {/* Employment quick facts */}
            <Card className="border-orange-100 dark:border-orange-500/20">
              <CardHeader><CardTitle className="text-base text-orange-700">{t('detail.employment')}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <Row icon={<Hash className="w-4 h-4" />} label={t('employee_id')} value={employee.employee_id} />
                <Row icon={<Building2 className="w-4 h-4" />} label={t('department')} value={deptName || t('detail.unassigned')} />
                <Row icon={<Calendar className="w-4 h-4" />} label={t('hire_date')} value={fmt(employee.hire_date)} />
              </CardContent>
            </Card>

            {/* Reporting */}
            <Card className="border-orange-100 dark:border-orange-500/20">
              <CardHeader><CardTitle className="text-base text-orange-700">{t('detail.reporting')}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t('manager')}</p>
                  {manager ? (
                    <button type="button" onClick={() => goToEmployee(manager.id)} className="flex items-center gap-2 hover:bg-orange-50 rounded-md px-1 py-0.5 -ml-1">
                      <Avatar className="w-7 h-7">
                        <AvatarImage src={manager.profile_picture_url || undefined} />
                        <AvatarFallback className="text-[10px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700">
                          {initials(`${manager.first_name || ''} ${manager.last_name || ''}`)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm text-foreground">{`${manager.first_name || ''} ${manager.last_name || ''}`.trim()}</span>
                      <span className="text-xs text-muted-foreground">{manager.position}</span>
                    </button>
                  ) : <p className="text-sm text-muted-foreground">{t('detail.top_level')}</p>}
                </div>
                {summary && summary.total_reports > 0 && (
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{summary.total_reports}</span> {t('detail.total_in_org')}
                    {' '}({t('detail.direct_count', { count: summary.direct_reports })})
                  </p>
                )}
                <div>
                  <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Users className="w-3 h-3" /> {t('detail.direct_reports', { count: directReports.length })}</p>
                  {directReports.length === 0 ? (
                    <p className="text-sm text-muted-foreground">{t('detail.none')}</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {directReports.map((r) => (
                        <button key={r.id} type="button" onClick={() => goToEmployee(r.id)}
                          className="flex items-center gap-2 bg-muted border border-border rounded-full pl-1 pr-3 py-1 hover:border-orange-300 dark:hover:border-orange-500/40">
                          <Avatar className="w-6 h-6">
                            <AvatarImage src={r.profile_picture_url || undefined} />
                            <AvatarFallback className="text-[10px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700">
                              {initials(`${r.first_name || ''} ${r.last_name || ''}`)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-xs text-foreground">{`${r.first_name || ''} ${r.last_name || ''}`.trim()}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Leave / Availability */}
            <Card className="border-orange-100 dark:border-orange-500/20">
              <CardHeader><CardTitle className="text-base text-orange-700">{t('detail.leave_availability')}</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {leave.length === 0 ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CalendarOff className="w-4 h-4" /> {t('detail.no_upcoming_leave')}
                  </div>
                ) : (
                  leave
                    .sort((a, b) => a.start_date.localeCompare(b.start_date))
                    .map((l, i) => {
                      const today = new Date().toISOString().slice(0, 10);
                      const ongoing = l.start_date <= today && (l.end_date || l.start_date) >= today;
                      return (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span className="capitalize text-foreground">{l.type || t('detail.leave')}</span>
                          <span className="text-muted-foreground">{fmt(l.start_date)} → {fmt(l.end_date)}</span>
                          {ongoing && <Badge className="bg-orange-100 dark:bg-orange-500/[0.12] text-orange-800 border border-orange-200 dark:border-orange-500/25 text-xs">{t('detail.now')}</Badge>}
                        </div>
                      );
                    })
                )}
              </CardContent>
            </Card>
          </div>

          {/* Data readiness — completeness for payroll & filings; in Draft it
              becomes the activation checklist (what's blocking Active). */}
          {readiness && (
            <ReadinessCard
              readiness={readiness}
              draft={isDraft}
              employeeName={employee.first_name || fullName}
              onFix={fixField}
            />
          )}

          {/* Danger zone — owner-only hard delete for DUPLICATE records (e.g. an
              import re-creating someone who already exists). Real departures go
              through offboarding; the backend refuses records linked to a login. */}
          {isTenantOwner && (
            <Card className="border-danger">
              <CardHeader>
                <CardTitle className="text-base text-danger">
                  {t('detail.danger_zone', { defaultValue: 'Danger zone' })}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground max-w-xl">
                  {t('detail.delete_record_hint', {
                    defaultValue:
                      'Permanently delete this record if it is a duplicate or was created by mistake. For a real employee leaving the company, use offboarding instead — records linked to a login account cannot be deleted.',
                  })}
                </p>
                <Button
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-500/10 shrink-0"
                  onClick={() => { setDeleteConfirm(''); setDeleteOpen(true); }}
                >
                  {t('detail.delete_record', { defaultValue: 'Delete employee record' })}
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* -------------------------------------------------- Lifecycle (onboarding/offboarding) */}
        {showLifecycleTab && lifecycle && employeeId && (
          <TabsContent value="lifecycle" className="space-y-6">
            <EmployeeLifecycleCard
              instance={lifecycle}
              onChanged={() => refreshLifecycle(employeeId)}
              onCompleted={async () => {
                // Completion/cancellation flips the employee status (and payroll
                // flag) — refetch everything the header + tabs derive from.
                refreshLifecycle(employeeId);
                refreshReadiness(employeeId);
                try { setEmployee(await getEmployee(employeeId)); } catch { /* keep current */ }
              }}
            />
          </TabsContent>
        )}

        {/* -------------------------------------------------- Personal */}
        <TabsContent value="personal" className="space-y-6">
          <PersonalCard employee={employee} canEdit={canOrganize} onSave={applyUpdate} />
        </TabsContent>

        {/* -------------------------------------------------- Identity & IDs */}
        {canOrganize && (
          <TabsContent value="identity" className="space-y-6">
            <IdentityCard employee={employee} canEdit={canOrganize} onSave={applyUpdate} />
          </TabsContent>
        )}

        {/* -------------------------------------------------- Employment */}
        <TabsContent value="employment" className="space-y-6">
          {/* Multi-entity workforce: every company this person works for that the
              viewer may see (fenced server-side, O-2). The card below is the
              PRIMARY engagement's terms (the employee record). */}
          <EngagementsCard
            employeeId={employee.id}
            personName={`${employee.first_name ?? ''} ${employee.last_name ?? ''}`.trim() || 'this person'}
            isSelf={isSelf}
          />
          <EmploymentCard employee={employee} canEdit={canOrganize} onSave={applyUpdate} />
        </TabsContent>

        {/* -------------------------------------------------- Job & Organization */}
        {canOrganize && (
          <TabsContent value="organization" className="space-y-6">
            {/* Organize (admins/managers): set this person's org level + manager */}
            <Card className="border-orange-100 dark:border-orange-500/20">
              <CardHeader>
                <CardTitle className="text-base text-orange-700 flex items-center gap-2">
                  <Network className="w-4 h-4" /> {t('detail.organize')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className={labelCls}>{t('department')}</label>
                    <select aria-label={t('department')} value={editDept} onChange={(e) => setEditDept(e.target.value)} disabled={!orgEdit}
                      className={inputCls}>
                      <option value="">{t('detail.unassigned_option')}</option>
                      {departments
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className={labelCls}>{t('detail.job_title')}</label>
                    <input aria-label={t('detail.job_title')} list="org-title-options" value={editTitle} disabled={!orgEdit}
                      onChange={(e) => setEditTitle(e.target.value)} placeholder={t('detail.job_title_placeholder')}
                      className={inputCls} />
                    <datalist id="org-title-options">
                      {titleOptions.map((tt) => <option key={tt} value={tt} />)}
                    </datalist>
                  </div>
                  <div className="space-y-1">
                    <label className={labelCls}>{t('detail.org_level')}</label>
                    <select aria-label={t('detail.org_level')} value={editLevel} onChange={(e) => setEditLevel(e.target.value)} disabled={!orgEdit}
                      className={inputCls}>
                      {ORG_LEVELS.map((l) => <option key={l} value={l}>{levelLabel(l)}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className={labelCls}>{t('detail.reports_to')}</label>
                    <select aria-label={t('detail.reports_to')} value={editManager} onChange={(e) => setEditManager(e.target.value)} disabled={!orgEdit}
                      className={inputCls}>
                      <option value="">{t('detail.no_manager_option')}</option>
                      {all
                        .filter((e) => e.id !== employee.id)
                        .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`))
                        .map((e) => (
                          <option key={e.id} value={e.id}>
                            {`${e.first_name || ''} ${e.last_name || ''}`.trim()}{e.position ? ` · ${e.position}` : ''}
                          </option>
                        ))}
                    </select>
                  </div>
                  {orgEdit && (editManager || '') !== (employee.manager_id || '') && (
                    <div className="space-y-1" data-testid="manager-effective">
                      <label className={labelCls} htmlFor="manager-effective-date">
                        {t('detail.manager_effective', { defaultValue: 'Takes effect' })}
                      </label>
                      <input id="manager-effective-date" type="date" className={inputCls}
                        value={editManagerFrom} onChange={(e) => setEditManagerFrom(e.target.value)} />
                      <p className="text-xs text-muted-foreground">
                        {t('detail.manager_effective_hint', {
                          defaultValue: 'Leave empty for today. A future date plans the move — the current manager keeps them until then.' })}
                      </p>
                    </div>
                  )}
                </div>
                <div className="mt-4 flex items-center gap-2">
                  {orgEdit ? (
                    <>
                      <Button onClick={saveOrg} disabled={!orgDirty || savingOrg}>
                        <Save className="w-4 h-4 mr-2" />
                        {savingOrg ? t('detail.saving') : t('detail.save')}
                      </Button>
                      <Button variant="outline" onClick={() => { resetOrg(); setOrgEdit(false); }} disabled={savingOrg}>
                        <X className="w-4 h-4 mr-1" /> {t('common.cancel', { defaultValue: 'Cancel' })}
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" onClick={() => setOrgEdit(true)}>
                      <Pencil className="w-4 h-4 mr-1" /> {t('common.edit', { defaultValue: 'Edit' })}
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {t('detail.level_hint')}
                </p>
              </CardContent>
            </Card>

            <WorkAssignmentCard employee={employee} canEdit={canOrganize} onSave={applyUpdate} />
          </TabsContent>
        )}

        {/* -------------------------------------------------- Payroll */}
        {showPayrollTab && (
          <TabsContent value="payroll" className="space-y-6">
            {/* Compensation (HR/owner only): assign to payroll + set/revise base salary */}
            {canManagePay && employeeId && (
              <CompensationCard
                employeeId={employeeId}
                fullName={fullName}
                email={employee.email}
                nationalId={(employee as any).national_id}
              />
            )}
            {canRunPayroll && employeeId && <PayrollProfileCard employeeId={employeeId} />}
            {/* Multi-entity only: the card hides itself when the tenant has
                one company, because cost then has nowhere else to go. */}
            {canRunPayroll && employeeId && (
              <CostAllocationCard employeeId={employeeId} canEdit={canRunPayroll} />
            )}
            {employeeId && <EmployeePayoutMethodsCard employeeId={employeeId} employeeName={fullName} />}
            {canRunPayroll && employeeId && (
              <SalaryAllocationCard employeeId={employeeId} canEdit={canProposeSplit} canApprove={canApproveSplit} />
            )}
          </TabsContent>
        )}

        {/* -------------------------------------------------- Documents */}
        {canOrganize && employeeId && (
          <TabsContent value="documents" className="space-y-6">
            {/* Required-vs-uploaded checklist (policy-driven); refetches when
                the documents card below reports a change. */}
            <RequiredDocumentsCard employeeId={employeeId} refreshKey={docsVersion} />
            <EmployeeDocumentsCard
              employeeId={employeeId}
              canManage={canOrganize}
              onChanged={() => setDocsVersion((v) => v + 1)}
            />
          </TabsContent>
        )}

        {/* -------------------------------------------------- Change history */}
        {showHistoryTab && employeeId && (
          <TabsContent value="history" className="space-y-6">
            <ChangeHistoryCard employeeId={employeeId} />
          </TabsContent>
        )}
      </Tabs>

      {deleteOpen && employee && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <Card className="w-full max-w-md border-danger">
            <CardHeader>
              <CardTitle className="text-base text-danger">
                {t('detail.delete_record', { defaultValue: 'Delete employee record' })}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t('detail.delete_record_warning', {
                  defaultValue:
                    'This permanently removes the record and cannot be undone. Payroll and other modules will drop this person from their rosters. Only do this for a duplicate or mistaken record.',
                })}
              </p>
              <div>
                <label className="text-sm font-medium text-foreground" htmlFor="delete-confirm-name">
                  {t('detail.delete_type_name', {
                    defaultValue: 'Type the employee\u2019s full name to confirm',
                  })}
                </label>
                <input
                  id="delete-confirm-name"
                  autoFocus
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  placeholder={`${employee.first_name} ${employee.last_name}`}
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-red-400"
                />
              </div>
              {/* A record with a login cannot be deleted on its own, and saying
                  so only after the attempt fails sends people away thinking the
                  product is broken. The account has to go WITH the record: left
                  behind, it can still sign in, and /me/profile self-provisions a
                  fresh employee record for any admin or owner in that state — so
                  the duplicate would simply come back. */}
              {employee.user_id && (
                <label className="flex items-start gap-2 rounded-lg border border-warning-strong/30 bg-warning/5 p-3 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={retireLogin}
                    onChange={(e) => setRetireLogin(e.target.checked)}
                  />
                  <span className="text-muted-foreground">
                    {t('detail.delete_retire_login', {
                      defaultValue:
                        'This record has a sign-in account. Disable that account too — required to delete a duplicate, and it stops the record being recreated the next time it signs in.',
                    })}
                  </span>
                </label>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleting}>
                  {t('common.cancel', { defaultValue: 'Cancel' })}
                </Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={
                    deleting ||
                    // Collapse internal/edge whitespace on BOTH sides: stored names
                    // can carry stray spaces (e.g. "Morris ") which HTML renders as a
                    // single space, so a strict compare would never match what the
                    // user sees and types.
                    deleteConfirm.trim().replace(/\s+/g, ' ').toLowerCase() !==
                      `${employee.first_name} ${employee.last_name}`.replace(/\s+/g, ' ').trim().toLowerCase()
                  }
                  onClick={async () => {
                    setDeleting(true);
                    try {
                      await deleteEmployeeRecord(employee.id, retireLogin);
                      toast.success(t('detail.record_deleted', { defaultValue: 'Employee record deleted.' }));
                      navigate(employeesBase);
                    } catch (e: any) {
                      toast.error(getApiErrorMessage(e, t('detail.update_failed')));
                      setDeleting(false);
                    }
                  }}
                >
                  {deleting
                    ? t('detail.deleting', { defaultValue: 'Deleting\u2026' })
                    : t('detail.delete_record', { defaultValue: 'Delete employee record' })}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default EmployeeDetailPage;
